import React from 'react';
import { Link } from 'react-router-dom';
import {
	Card, Row, Col, Input, Button, Table, Modal, Form, Select, Spin, Divider,
	message, Tag, Typography, Tooltip,
} from 'antd';
import {
	PlusOutlined, FormOutlined, CloseSquareOutlined,
	ExclamationCircleOutlined, RedoOutlined, SignatureOutlined,
	SearchOutlined, EnvironmentOutlined, SaveOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import {
	initHandler, ajaxViewHandler, ajaxHandler, setConfig,
} from '../../store/actions';
import Config from '../../Config';
import { Helmet } from 'react-helmet';
import ErrorAlertModal from '../../components/ErrorAlertModal';

const { Text } = Typography;

const PERAN_MAP = {
	kepala_upt:     { label: 'Kepala UPTPPD Samsat',             color: '#08979c', bg: '#e6fffb', border: '#87e8de' },
	polda:          { label: 'Kasi STNK Ditlantas Polda',        color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f' },
	jr:             { label: 'Penanggung Jawab PT Jasa Raharja', color: '#d46b08', bg: '#fff7e6', border: '#ffd591' },
	kasi_tu:        { label: 'Kasi TU',                          color: '#c41d7f', bg: '#fff0f6', border: '#ffadd2' },
	uptppd:         { label: 'Kasi Penetapan UPTPPD Samsat',    color: '#1e4d8a', bg: '#e6f0fb', border: '#91b4e8' },
	kasi_pelaporan: { label: 'Kasi Pelaporan',                   color: '#1d39c4', bg: '#f0f5ff', border: '#adc6ff' },
	bendahara:      { label: 'Bendahara Penerima',               color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7' },
};

const PERAN_OPTIONS = [
	{ value: 'kepala_upt',     label: 'Kepala UPTPPD Samsat' },
	{ value: 'polda',          label: 'Kasi STNK Ditlantas Polda Kalteng' },
	{ value: 'jr',             label: 'Penanggung Jawab PT Jasa Raharja' },
	{ value: 'kasi_tu',        label: 'Kasi TU' },
	{ value: 'uptppd',         label: 'Kasi Penetapan UPTPPD Samsat' },
	{ value: 'kasi_pelaporan', label: 'Kasi Pelaporan' },
	{ value: 'bendahara',      label: 'Bendahara Penerima' },
];

const configClass = {
	title : 'Data Tanda Tangan Laporan',
	apiUrl: {
		data     : 'master/api/ttd-laporan/',
		kabupaten: 'master/api/kabupaten/',
	},
	pageUrl: { form: '/admin/tanda_tangan_laporan/form/' },
};

// Dataset ini kecil (maks ±14 kabupaten x 5 peran = 70 baris) - diambil
// sekaligus tanpa pagination berbasis baris, supaya grouping per kabupaten
// di bawah tidak pernah kepotong di tengah halaman.
const LIST_LIMIT = 500;

// Total lebar kolom tabel per grup (260 Peran + 240 Nama + 240 Jabatan +
// 160 NIP + 80 Aksi) - dipakai sbg scroll.x supaya SEMUA tabel grup punya
// lebar yang identik persis, bukan auto-mengecil/membesar ikut panjang
// konten tiap grup (lihat diskusi 2026-10-07, screenshot Lamandau vs
// Palangka Raya beda lebar).
const GROUP_TABLE_WIDTH = 980;

const config = new Config();

class DataTandaTanganLaporan extends React.Component {
	formModalRef = React.createRef();

	constructor(props) {
		super(props);
		this.state = {
			errMsg      : '',
			errTitle    : '',
			loadingData : false,
			total       : 0,
			searchText  : '',
			dataSource  : [],
			isMobile    : window.innerWidth <= 768,

			// modal edit
			modalVisible       : false,
			modalEditId        : null,
			modalLoadingData   : false,
			modalLoadingSubmit : false,
			modalListKabupaten : [],
			modalLoadingKabupaten: false,
		};
		this._searchTimer = null;
	}

	UNSAFE_componentWillMount() {
		this.props.initHandler();
	}

	componentDidMount() {
		this.getData();
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
		clearTimeout(this._searchTimer);
	}

	// ── Data list ────────────────────────────────────────────────
	async getData(search = this.state.searchText) {
		this.setState({ loadingData: true });
		this.props.ajaxViewHandler(
			'get',
			`${configClass.apiUrl.data}?limit=${LIST_LIMIT}&offset=0&search=${search}`,
		).then(() => {
			if (this.props.isSuccess) {
				const data = this.props.responseMsg;
				this.setState({ dataSource: data.results, total: data.count });
			} else {
				message.error({ content: 'Gagal memuat data', duration: 2, key: 'toast' });
			}
			this.setState({ loadingData: false });
		}).catch(() => {
			message.error({ content: 'Gagal request data', duration: 2, key: 'toast' });
			this.setState({ loadingData: false });
		});
	}

	handleSearchChange = (e) => {
		const val = e.target.value;
		this.setState({ searchText: val });
		clearTimeout(this._searchTimer);
		this._searchTimer = setTimeout(() => this.getData(val), 400);
	}

	// ── Grouping per kabupaten/kota ────────────────────────────────
	_groupByKabupaten() {
		// Urutan baris dalam tiap grup ngikut urutan PERAN_OPTIONS (bukan
		// urutan default backend yang alfabetis per key peran) - peran yang
		// gak ada di daftar (harusnya gak pernah terjadi) ditaruh paling
		// akhir, bukan hilang.
		const peranIndex = PERAN_OPTIONS.reduce((acc, o, i) => ({ ...acc, [o.value]: i }), {});
		const groups = new Map();
		for (const row of this.state.dataSource) {
			const key = row.kabupaten || row.kabupaten_nama || '-';
			if (!groups.has(key)) {
				groups.set(key, { kabupaten_nama: row.kabupaten_nama || '-', rows: [] });
			}
			groups.get(key).rows.push(row);
		}
		for (const g of groups.values()) {
			g.rows.sort((a, b) => {
				const ia = peranIndex[a.peran] ?? PERAN_OPTIONS.length;
				const ib = peranIndex[b.peran] ?? PERAN_OPTIONS.length;
				return ia - ib;
			});
		}
		return Array.from(groups.values())
			.sort((a, b) => a.kabupaten_nama.localeCompare(b.kabupaten_nama, 'id'));
	}

	// ── Delete ───────────────────────────────────────────────────
	showDeleteConfirm(id) {
		Modal.confirm({
			title  : 'Konfirmasi Hapus?',
			icon   : <ExclamationCircleOutlined />,
			content: 'Yakin menghapus data tanda tangan ini?',
			okText : 'Ya, Hapus',
			okType : 'danger',
			cancelText: 'Batal',
			onOk: () => this.prosesHapus(id),
		});
	}

	prosesHapus(id) {
		message.loading({ content: 'Menghapus...', key: 'toast' });
		this.props.ajaxHandler('delete', configClass.apiUrl.data + id + '/')
			.then(() => {
				if (this.props.isSuccess) {
					message.success({ content: 'Data berhasil dihapus', duration: 2, key: 'toast' });
					this.getData();
				} else {
					message.error({ content: 'Gagal menghapus data', duration: 2, key: 'toast' });
					this.setState({ errTitle: 'Gagal hapus', errMsg: this.props.responseMsg });
				}
			})
			.catch(() => message.error({ content: 'Kesalahan jaringan', duration: 2, key: 'toast' }));
	}

	// ── Modal Edit ───────────────────────────────────────────────
	loadModalKabupaten() {
		this.setState({ modalLoadingKabupaten: true });
		return this.props.ajaxViewHandler('get', configClass.apiUrl.kabupaten + '?limit=500')
			.then(() => {
				if (this.props.isSuccess) {
					const res = this.props.responseMsg;
					const list = res.results || (Array.isArray(res) ? res : []);
					this.setState({ modalListKabupaten: list });
				}
				this.setState({ modalLoadingKabupaten: false });
			})
			.catch(() => this.setState({ modalLoadingKabupaten: false }));
	}

	openEditModal(id) {
		this.setState({ modalVisible: true, modalEditId: id, modalLoadingData: true });
		if (this.formModalRef.current) this.formModalRef.current.resetFields();

		// serialkan: kabupaten dulu → detail, agar responseMsg tidak bentrok
		const loadKabupaten = this.state.modalListKabupaten.length > 0
			? Promise.resolve()
			: this.loadModalKabupaten();

		loadKabupaten
			.then(() => this.props.ajaxViewHandler('get', configClass.apiUrl.data + id + '/'))
			.then(() => {
				if (this.props.isSuccess) {
					const d = this.props.responseMsg;
					// loadingData: false dulu agar form mount, lalu isi di callback
					this.setState({ modalLoadingData: false }, () => {
						if (this.formModalRef.current) {
							this.formModalRef.current.setFieldsValue({
								kabupaten: d.kabupaten,
								peran    : d.peran,
								nama     : d.nama,
								jabatan  : d.jabatan || '',
								nip      : d.nip || '',
							});
						}
					});
				} else {
					this.setState({ modalLoadingData: false });
					message.error({ content: 'Gagal memuat data', duration: 2, key: 'toast' });
				}
			})
			.catch(() => {
				this.setState({ modalLoadingData: false });
				message.error({ content: 'Gagal memuat data', duration: 2, key: 'toast' });
			});
	}

	closeEditModal = () => {
		this.setState({ modalVisible: false, modalEditId: null });
		if (this.formModalRef.current) this.formModalRef.current.resetFields();
	}

	handleModalSubmit = (values) => {
		const { modalEditId } = this.state;
		const payload = {
			...values,
			jabatan: values.jabatan != null ? values.jabatan : '',
			nip    : values.nip != null ? values.nip : '',
		};
		this.setState({ modalLoadingSubmit: true });
		message.loading({ content: 'Menyimpan...', key: 'toast' });

		this.props.ajaxHandler('put', configClass.apiUrl.data + modalEditId + '/', payload)
			.then(() => {
				this.setState({ modalLoadingSubmit: false });
				if (this.props.isSuccess) {
					message.success({ content: 'Data berhasil disimpan', duration: 2, key: 'toast' });
					this.closeEditModal();
					this.getData();
				} else {
					message.destroy();
					this.setState({
						errTitle: 'Gagal simpan',
						errMsg  : this.props.responseMsg,
					});
				}
			})
			.catch(() => {
				this.setState({ modalLoadingSubmit: false });
				message.error({ content: 'Kesalahan jaringan', duration: 2, key: 'toast' });
			});
	}

	// ── Render helpers ───────────────────────────────────────────
	_renderPeranBadge(val) {
		const p = PERAN_MAP[val];
		if (!p) return <Text type="secondary">{val}</Text>;
		return (
			<span style={{
				display    : 'inline-block',
				padding    : '2px 10px',
				borderRadius: 20,
				fontSize   : 11,
				fontWeight : 600,
				color      : p.color,
				background : p.bg,
				border     : `1px solid ${p.border}`,
				whiteSpace : 'nowrap',
			}}>
				{p.label}
			</span>
		);
	}

	_buildColumns() {
		return [
			{
				title    : 'Peran',
				dataIndex: 'peran',
				key      : 'peran',
				width    : 200,
				align : 'left',
				render   : (val) => this._renderPeranBadge(val),
			},
			{
				title : 'Nama',
				dataIndex: 'nama',
				key   : 'nama',
				width : 300,
				align : 'left',
				ellipsis: true,
				render: (val) => <Text style={{ fontSize: 13 }}>{val || '-'}</Text>,
			},
			{
				title : 'Jabatan',
				dataIndex: 'jabatan',
				key   : 'jabatan',
				width : 180,
				align : 'left',
				ellipsis: true,
				render: (val) => <Text type="secondary" style={{ fontSize: 12 }}>{val || '-'}</Text>,
			},
			{
				title    : 'NIP',
				dataIndex: 'nip',
				key      : 'nip',
				width    : 160,
				align : 'left',
				render   : (val) => val
					? <Tag style={{ borderRadius: 20, fontSize: 11, fontFamily: 'monospace', fontWeight: 600 }}>{val}</Tag>
					: <Text type="secondary">-</Text>,
			},
			{
				title : 'Aksi',
				key   : 'aksi',
				width : 80,
				fixed : 'right',
				align : 'center',
				render: (_, row) => (
					<div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
						<Tooltip title="Edit">
							<Button
								size="small" shape="circle" type="primary" ghost
								onClick={() => this.openEditModal(row.id)}
							>
								<FormOutlined />
							</Button>
						</Tooltip>
						<Tooltip title="Hapus">
							<Button size="small" shape="circle" danger onClick={() => this.showDeleteConfirm(row.id)}>
								<CloseSquareOutlined />
							</Button>
						</Tooltip>
					</div>
				),
			},
		];
	}

	renderMobileCard(row) {
		const p = PERAN_MAP[row.peran];
		return (
			<Card
				key={row.id}
				style={{ borderRadius: 14, marginBottom: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0', overflow: 'hidden' }}
				bodyStyle={{ padding: 0 }}
			>
				<div style={{
					background  : 'linear-gradient(135deg, #0f284711, #1e4d8a11)',
					borderBottom: '1px solid #d0dff5',
					padding     : '10px 16px',
					display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
				}}>
					<div style={{ flex: 1, minWidth: 0 }}>
						{p ? (
							<span style={{
								display: 'inline-block', padding: '2px 10px',
								borderRadius: 20, fontSize: 11, fontWeight: 600,
								color: p.color, background: p.bg, border: `1px solid ${p.border}`,
							}}>
								{p.label}
							</span>
						) : <Text type="secondary">{row.peran}</Text>}
					</div>
					<div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
						<Button
							size="middle" shape="circle" type="primary" ghost
							onClick={() => this.openEditModal(row.id)}
						>
							<FormOutlined />
						</Button>
						<Button size="middle" shape="circle" danger onClick={() => this.showDeleteConfirm(row.id)}>
							<CloseSquareOutlined />
						</Button>
					</div>
				</div>
				<div style={{ padding: '10px 16px' }}>
					<div style={{ marginBottom: 4 }}>
						<Text type="secondary" style={{ fontSize: 11 }}>Nama</Text>
						<div style={{ fontWeight: 600, fontSize: 14, color: '#222' }}>{row.nama || '-'}</div>
					</div>
					{row.jabatan && (
						<div style={{ marginBottom: 4 }}>
							<Text type="secondary" style={{ fontSize: 11 }}>Jabatan</Text>
							<div style={{ fontSize: 13, color: '#444' }}>{row.jabatan}</div>
						</div>
					)}
					{row.nip && (
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>NIP</Text>
							<div>
								<Tag style={{ borderRadius: 20, fontSize: 11, fontFamily: 'monospace', fontWeight: 600, marginTop: 2 }}>
									{row.nip}
								</Tag>
							</div>
						</div>
					)}
				</div>
			</Card>
		);
	}

	renderEditModal() {
		const {
			modalVisible, modalLoadingData, modalLoadingSubmit,
			modalListKabupaten, modalLoadingKabupaten, isMobile,
		} = this.state;

		const inputStyle = { borderRadius: 8, height: 38 };

		const modalBody = (
			<div>
				{/* Custom header */}
				<div style={{
					background  : 'linear-gradient(135deg, #0f2847 0%, #1e4d8a 60%, #0a3d6b 100%)',
					borderRadius: isMobile ? '0' : '8px 8px 0 0',
					padding     : isMobile ? '16px 20px' : '20px 28px',
					display     : 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
				}}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
						<div style={{
							width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
							borderRadius: 10, background: 'rgba(255,255,255,0.15)', flexShrink: 0,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
						}}>
							<SignatureOutlined style={{ color: '#fff', fontSize: isMobile ? 16 : 20 }} />
						</div>
						<div>
							<div style={{ color: '#fff', fontWeight: 700, fontSize: isMobile ? 14 : 16 }}>
								Edit Tanda Tangan Laporan
							</div>
							{!isMobile && (
								<div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginTop: 2 }}>
									Ubah data TTD untuk kabupaten/kota ini
								</div>
							)}
						</div>
					</div>
					<Button
						type="text"
						icon={<CloseCircleOutlined style={{ fontSize: 18 }} />}
						onClick={this.closeEditModal}
						style={{ color: 'rgba(255,255,255,0.8)', flexShrink: 0, padding: 4 }}
					/>
				</div>

				{/* Body */}
				<div style={{ padding: isMobile ? '16px 16px 0' : '24px 28px 0' }}>
					{modalLoadingData ? (
						<div style={{ textAlign: 'center', padding: isMobile ? 40 : 56 }}>
							<Spin size="large" />
							<div style={{ marginTop: 12, color: '#888', fontSize: 13 }}>Memuat data...</div>
						</div>
					) : (
						<Form
							ref={this.formModalRef}
							layout="vertical"
							onFinish={this.handleModalSubmit}
						>
							<Form.Item
								label="Kabupaten / Kota"
								name="kabupaten"
								rules={[{ required: true, message: 'Kabupaten / Kota wajib dipilih' }]}
							>
								<Select
									showSearch
									placeholder="Pilih Kabupaten / Kota"
									optionFilterProp="children"
									loading={modalLoadingKabupaten}
									style={{ width: '100%' }}
								>
									{modalListKabupaten.map(k => (
										<Select.Option key={k.id} value={k.id}>{k.name}</Select.Option>
									))}
								</Select>
							</Form.Item>

							<Form.Item
								label="Peran"
								name="peran"
								rules={[{ required: true, message: 'Peran wajib dipilih' }]}
							>
								<Select placeholder="Pilih peran TTD">
									{PERAN_OPTIONS.map(o => (
										<Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
									))}
								</Select>
							</Form.Item>

							<Form.Item
								label="Nama"
								name="nama"
								rules={[{ required: true, message: 'Nama wajib diisi' }]}
							>
								<Input placeholder="Nama lengkap pejabat" style={inputStyle} />
							</Form.Item>

							<Form.Item label="Jabatan" name="jabatan">
								<Input placeholder="Jabatan (opsional)" style={inputStyle} />
							</Form.Item>

							<Form.Item label="NIP" name="nip">
								<Input placeholder="NIP (opsional)" style={inputStyle} />
							</Form.Item>
						</Form>
					)}
				</div>

				{/* Footer */}
				{!modalLoadingData && (
					<div>
						<Divider style={{ margin: '16px 0 0' }} />
						<div style={{
							padding        : isMobile ? '14px 16px' : '16px 28px',
							display        : 'flex',
							flexDirection  : isMobile ? 'column-reverse' : 'row',
							justifyContent : isMobile ? 'stretch' : 'flex-end',
							gap            : 10,
							background     : '#fafafa',
							borderRadius   : isMobile ? '0' : '0 0 8px 8px',
						}}>
							<Button
								icon={<CloseCircleOutlined />}
								onClick={this.closeEditModal}
								disabled={modalLoadingSubmit}
								style={{
									borderRadius: 8, height: 40,
									width: isMobile ? '100%' : 'auto',
								}}
							>
								Batal
							</Button>
							<Button
								type="primary"
								icon={<SaveOutlined />}
								loading={modalLoadingSubmit}
								onClick={() => this.formModalRef.current && this.formModalRef.current.submit()}
								style={{
									background  : 'linear-gradient(135deg, #0f2847, #1e4d8a)',
									border      : 'none',
									borderRadius: 8,
									fontWeight  : 600,
									height      : 40,
									width       : isMobile ? '100%' : 'auto',
								}}
							>
								Simpan Perubahan
							</Button>
						</div>
					</div>
				)}
			</div>
		);

		return (
			<Modal
				visible={modalVisible}
				onCancel={this.closeEditModal}
				footer={null}
				title={null}
				closable={false}
				width={isMobile ? 'calc(100vw - 16px)' : 580}
				style={isMobile ? { top: 12, margin: '0 8px' } : { top: 60 }}
				bodyStyle={{ padding: 0 }}
				maskClosable={!modalLoadingSubmit}
				destroyOnClose
			>
				{modalBody}
			</Modal>
		);
	}

	renderGroupHeader(nama) {
		return (
			<div style={{
				display: 'flex', alignItems: 'center', gap: 8,
				padding: '8px 16px',
				background: 'linear-gradient(135deg, #0f284714, #1e4d8a14)',
				borderLeft: '3px solid #1e4d8a',
			}}>
				<EnvironmentOutlined style={{ color: '#1e4d8a' }} />
				<Text strong style={{ fontSize: 14, color: '#0f2847' }}>{nama}</Text>
			</div>
		);
	}

	render() {
		const { errMsg, errTitle, loadingData, dataSource, total, isMobile, searchText } = this.state;
		const groups = this._groupByKabupaten();

		return (
			<div>
				<Helmet>
					<title>{configClass.title + ' | ' + config.get_site_info().name}</title>
					<style>{`
						.ttd-lap-wrap .ant-table-thead > tr > th {
							background: linear-gradient(135deg,#0f284711,#1e4d8a11) !important;
							font-weight: 700; font-size: 13px; color: #0f2847;
							border-bottom: 2px solid #d0dff5;
						}
						.ttd-lap-wrap .ant-table-tbody > tr > td { padding: 10px 16px; vertical-align: middle; }
						.ttd-lap-wrap .ant-table-tbody > tr:hover > td { background: #e8f0fb !important; }
						.ttd-lap-wrap .ant-form-item-label > label { font-weight: 600; color: #374151; font-size: 13px; }
					`}</style>
				</Helmet>

				{/* Error global */}
				<ErrorAlertModal
					visible={errMsg !== ''}
					title={errTitle}
					error={errMsg}
					onClose={() => this.setState({ errTitle: '', errMsg: '' })}
				/>

				{/* Modal edit */}
				{this.renderEditModal()}

				<Card
					className="ttd-lap-wrap"
					style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden' }}
					bodyStyle={{ padding: 0 }}
				>
					{/* Header */}
					<div style={{
						padding       : isMobile ? '14px 16px' : '16px 20px',
						background    : 'linear-gradient(135deg, #0f2847, #1e4d8a)',
						display       : 'flex', alignItems: 'center',
						justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
							<div style={{
								width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
								borderRadius: 10, background: 'rgba(255,255,255,0.15)',
								display: 'flex', alignItems: 'center', justifyContent: 'center',
							}}>
								<SignatureOutlined style={{ color: '#fff', fontSize: isMobile ? 16 : 20 }} />
							</div>
							<div>
								<div style={{ color: '#fff', fontWeight: 700, fontSize: isMobile ? 14 : 16 }}>
									Tanda Tangan Laporan
								</div>
								{!isMobile && (
									<div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
										TTD per Kabupaten/Kota untuk Berita Acara Batal SKPD
									</div>
								)}
							</div>
						</div>
						<div style={{ display: 'flex', gap: 8 }}>
							<Tooltip title="Refresh">
								<Button
									icon={<RedoOutlined />}
									onClick={() => this.getData()}
									loading={loadingData}
									style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}
								>
									{!isMobile && 'Refresh'}
								</Button>
							</Tooltip>
							<Link to={configClass.pageUrl.form}>
								<Button
									type="primary" icon={<PlusOutlined />}
									style={{ borderRadius: 8, background: '#fff', color: '#0f2847', border: 'none', fontWeight: 600 }}
								>
									{isMobile ? 'Tambah' : 'Tambah TTD'}
								</Button>
							</Link>
						</div>
					</div>

					{/* Search bar */}
					<div style={{
						padding     : isMobile ? '10px 12px' : '12px 20px',
						borderBottom: '1px solid #f0f0f0', background: '#fff',
						display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
					}}>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{loadingData ? 'Memuat...' : `${total || 0} data · ${groups.length} kabupaten/kota`}
						</Text>
						<Input
							placeholder="Cari nama, jabatan, NIP..."
							allowClear
							prefix={<SearchOutlined style={{ color: '#bbb' }} />}
							style={{ borderRadius: 8, maxWidth: isMobile ? '100%' : 320, width: isMobile ? '100%' : 'auto' }}
							value={searchText}
							onChange={this.handleSearchChange}
						/>
					</div>

					{/* Konten - dikelompokkan per kabupaten/kota */}
					<div style={{ padding: isMobile ? 12 : 0 }}>
						{loadingData ? (
							<div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Memuat data...</div>
						) : dataSource.length === 0 ? (
							<div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Tidak ada data</div>
						) : isMobile ? (
							groups.map((g, gi) => (
								<div key={g.kabupaten_nama} style={{ marginBottom: 16 }}>
									<div style={{ marginBottom: 8 }}>{this.renderGroupHeader(g.kabupaten_nama)}</div>
									{g.rows.map(row => this.renderMobileCard(row))}
								</div>
							))
						) : (
							groups.map((g, gi) => (
								<div
									key={g.kabupaten_nama}
									style={{
										marginBottom: gi < groups.length - 1 ? 20 : 0,
										border      : '1px solid #e8e8e8',
										borderRadius: 10,
										overflow    : 'hidden',
									}}
								>
									{this.renderGroupHeader(g.kabupaten_nama)}
									<Table
										size="small"
										showHeader
										tableLayout="fixed"
										columns={this._buildColumns()}
										dataSource={g.rows}
										rowKey="id"
										scroll={{ x: GROUP_TABLE_WIDTH }}
										pagination={false}
									/>
								</div>
							))
						)}
					</div>
				</Card>
			</div>
		);
	}
}

const mapStateToProps = (state) => state.configReducers;

export default connect(mapStateToProps, {
	initHandler, setConfig, ajaxViewHandler, ajaxHandler,
})(DataTandaTanganLaporan);
